import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { viewParentDetails } from '../../slice/parentSlice'
import { useLocation, useNavigate } from 'react-router-dom'
import moment from 'moment'

const ViewParentDetail = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()

    // Get parentId from state
    const parentId = location.state?.parentId

    const { parentDetail } = useSelector(state => state.parent);

    useEffect(() => {
        if (!parentId) {
            navigate('/parents')
            return
        }
        dispatch(viewParentDetails({ id: parentId }))
    }, [parentId, dispatch, navigate])

    if (!parentDetail) {
        return (
            <div className="text-center py-5">
                <i className="fe-alert-circle" style={{ fontSize: '48px', color: '#dc3545' }}></i>
                <h5 className="mt-3">No data found</h5>
                <button
                    className="btn btn-primary mt-3"
                    onClick={() => navigate('/parents')}
                >
                    Go Back to Parents
                </button>
            </div>
        )
    }

    const {
        first_name,
        last_name,
        username,
        email,
        country_code,
        phone_number,
        dob,
        lang,
        // is_verified,
        is_active,
        is_deleted,
        createdAt,
        updatedAt,
        profile_picture,
        emirates_id_front,
        emirates_id_back,
    } = parentDetail

    return (
        <div className="container-fluid px-4 py-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-semibold mb-0">Parent Details</h4>
                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/parents')}
                    style={{
                        padding: '8px 20px',
                        fontSize: '14px',
                        borderRadius: '6px'
                    }}
                >
                    ← Back to List
                </button>
            </div>

            {/* Profile Card */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '8px' }}>
                <div className="card-body p-4 d-flex align-items-center gap-4">
                    <img
                        src={profile_picture || '/assets/avatar.png'}
                        alt="Profile"
                        style={{
                            width: '100px',
                            height: '100px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: '3px solid #f8f9fa'
                        }}
                    />

                    <div>
                        <h5 className="mb-1 fw-semibold">
                            {(first_name || last_name) ? `${first_name || ''} ${last_name || ''}`.trim() : 'Unknown User'}
                        </h5>
                        {username && <p className="mb-2 text-muted">@{username}</p>}
                        <div className="d-flex gap-2 mt-2">
                            <span className={`badge ${is_active ? 'bg-success' : 'bg-secondary'}`}>
                                {is_active ? 'Active' : 'Inactive'}
                            </span>
                            {is_deleted === 1 && (
                                <span className="badge bg-danger">Deleted</span>
                            )}
                            {/* <span className={`badge ${is_verified ? 'bg-primary' : 'bg-warning text-dark'}`}>
                                {is_verified ? 'Verified' : 'Not Verified'}
                            </span> */}
                        </div>
                    </div>
                </div>
            </div>

            {/* Details Section */}
            <div className="row">
                {/* Personal Info */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            Personal Information
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Full Name</small>
                                <strong>{(first_name || last_name) ? `${first_name || ''} ${last_name || ''}`.trim() : 'Unknown User'}</strong>
                            </div>
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Date of Birth</small>
                                <strong>{dob ? moment(dob).format('DD MMM YYYY') : 'NA'}</strong>
                            </div>
                            <div>
                                <small className="text-muted d-block mb-1">Language</small>
                                <strong>{lang?.toUpperCase() || 'NA'}</strong>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Contact Info */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            Contact Information
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Email</small>
                                <strong>{email || 'NA'}</strong>
                            </div>
                            <div>
                                <small className="text-muted d-block mb-1">Phone</small>
                                <strong>
                                    {country_code && phone_number
                                        ? `${country_code} ${phone_number}`
                                        : 'NA'}
                                </strong>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Account Info */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            Account Information
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Joined On</small>
                                <strong>
                                    {createdAt
                                        ? moment(createdAt).local().format('DD MMM YYYY, hh:mm A')
                                        : 'NA'}
                                </strong>
                            </div>
                            <div>
                                <small className="text-muted d-block mb-1">Last Updated</small>
                                <strong>
                                    {updatedAt
                                        ? moment(updatedAt).local().format('DD MMM YYYY, hh:mm A')
                                        : 'NA'}
                                </strong>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Emirates ID */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            Emirates ID Documents
                        </div>
                        <div className="card-body">
                            {(emirates_id_front || emirates_id_back) ? (
                                <div className="row g-3">
                                    {emirates_id_front && (
                                        <div className="col-md-6">
                                            <small className="text-muted d-block mb-2">Front Side</small>
                                            <img
                                                src={emirates_id_front}
                                                alt="Emirates ID Front"
                                                onError={(e) => {
                                                    e.currentTarget.onerror = null;
                                                    e.currentTarget.src = process.env.PUBLIC_URL + '/assets/images/default-doc.png';
                                                }}
                                                style={{
                                                    width: '100%',
                                                    borderRadius: '6px',
                                                    border: '1px solid #dee2e6',
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => window.open(emirates_id_front, '_blank')}
                                            />
                                        </div>
                                    )}

                                    {emirates_id_back && (
                                        <div className="col-md-6">
                                            <small className="text-muted d-block mb-2">Back Side</small>
                                            <img
                                                src={emirates_id_back}
                                                onError={(e) => {
                                                    e.currentTarget.onerror = null;
                                                    e.currentTarget.src = process.env.PUBLIC_URL + '/assets/images/default-doc.png';
                                                }}
                                                alt="Emirates ID Back"
                                                style={{
                                                    width: '100%',
                                                    borderRadius: '6px',
                                                    border: '1px solid #dee2e6',
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => window.open(emirates_id_back, '_blank')}
                                            />
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <i className="fe-file-text" style={{ fontSize: '48px', color: '#dee2e6' }}></i>
                                    <p className="text-muted mt-2 mb-0">No documents uploaded</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ViewParentDetail;